import type { TFunction } from 'i18next';

/**
 * The client mirror of `backend/schemas/technicians.schema.ts`. It exists so an illogical record
 * is refused where the person is looking — under the field — instead of coming back as a 400 whose
 * zod `details` are English paths written for a log. The API stays the authority: this only
 * catches what a round trip should never have been spent on.
 */

/** What the form holds, reduced to what a rule reads. */
export interface ValidatableForm {
  firstName: string;
  lastName: string;
  company: string;
  phone: string;
  email: string;
  countryCode: string;
  password: string;
  geoAreaIds: number[];
  specialtyIds: number[];
}

export type ValidatedField = keyof ValidatableForm;

export type ValidationCode =
  | 'FIRST_NAME_REQUIRED'
  | 'LAST_NAME_REQUIRED'
  | 'NAME_TOO_LONG'
  | 'COMPANY_REQUIRED'
  | 'PHONE_REQUIRED'
  | 'PHONE_INVALID'
  | 'EMAIL_REQUIRED'
  | 'EMAIL_INVALID'
  | 'COUNTRY_REQUIRED'
  | 'AREAS_REQUIRED'
  | 'SPECIALTIES_REQUIRED'
  | 'PASSWORD_TOO_SHORT'
  | 'PASSWORD_TOO_LONG';

export type FormErrors = Partial<Record<ValidatedField, ValidationCode>>;

/** Visual order of the card stack: identity, then scope, then skills. Drives the focus jump. */
export const FIELD_ORDER: ValidatedField[] = [
  'firstName',
  'lastName',
  'company',
  'phone',
  'email',
  'password',
  'countryCode',
  'geoAreaIds',
  'specialtyIds',
];

/** Both halves of the pair a `<Label htmlFor>` and an `aria-describedby` need. */
export const controlId = (field: ValidatedField): string => `technician-${field}`;
export const errorId = (field: ValidatedField): string => `technician-${field}-error`;

// `users.username` is a 120-char `name`, and the login is "Prénom NOM": the pair is what overflows.
const NAME_MAX = 120;

// Deliberately looser than zod's own e-mail regex and stricter than `type="email"`: a domain with
// no dot ("jean@globe") is the mistake worth catching, and the API re-checks the rest.
const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

// The schema's regex, character for character.
const PHONE = /^[+0-9][0-9 ().\-/]*$/;

// bcrypt stops at 72 bytes, so a longer password has a tail nobody ever checks. Counted in bytes,
// never in characters: "é" costs two.
const byteLength = (value: string): number => new TextEncoder().encode(value).length;

/**
 * Every rule the record enforces before it sends anything, as a pure map of field to code.
 * `creating` is not decoration: company, phone, country, scope and specialties are blocking on a
 * new record and merely indicative on an old one. A record written before those fields existed
 * must stay saveable — otherwise deactivating its account demands filling three of them first.
 */
export const formErrors = (form: ValidatableForm, creating: boolean): FormErrors => {
  const errors: FormErrors = {};
  const firstName = form.firstName.trim();
  const lastName = form.lastName.trim();
  const phone = form.phone.trim();
  const email = form.email.trim();

  if (!firstName) errors.firstName = 'FIRST_NAME_REQUIRED';
  if (!lastName) errors.lastName = 'LAST_NAME_REQUIRED';
  // The message lands on the longer half — the one actually worth shortening.
  if (firstName && lastName && firstName.length + 1 + lastName.length > NAME_MAX) {
    errors[lastName.length >= firstName.length ? 'lastName' : 'firstName'] = 'NAME_TOO_LONG';
  }

  // A phone that is present must still look like one, whichever direction we are in: a value
  // typed wrong is a mistake to catch, while a value never typed is a field nobody filled.
  if (phone && (!PHONE.test(phone) || phone.length < 6 || phone.length > 30)) errors.phone = 'PHONE_INVALID';

  if (!email) errors.email = 'EMAIL_REQUIRED';
  else if (!EMAIL.test(email) || email.length > 255) errors.email = 'EMAIL_INVALID';

  if (creating) {
    // Empty is covered by the minimum: "at least 8 characters" is true of an empty box too.
    if (form.password.length < 8) errors.password = 'PASSWORD_TOO_SHORT';
    else if (byteLength(form.password) > 72) errors.password = 'PASSWORD_TOO_LONG';
    if (!form.company.trim()) errors.company = 'COMPANY_REQUIRED';
    if (!phone) errors.phone = 'PHONE_REQUIRED';
    if (!form.countryCode) errors.countryCode = 'COUNTRY_REQUIRED';
    if (form.geoAreaIds.length === 0) errors.geoAreaIds = 'AREAS_REQUIRED';
    if (form.specialtyIds.length === 0) errors.specialtyIds = 'SPECIALTIES_REQUIRED';
  }

  return errors;
};

/** Fields the client requires, so the label can carry the asterisk it owes the person. */
const REQUIRED_ON_CREATION_ONLY: ValidatedField[] = [
  'company',
  'phone',
  'countryCode',
  'geoAreaIds',
  'specialtyIds',
  'password',
];

export const isRequired = (field: ValidatedField, creating: boolean): boolean =>
  REQUIRED_ON_CREATION_ONLY.includes(field) ? creating : true;

/**
 * Code to sentence. A switch of string literals rather than a lookup keyed by the code: the
 * translation function may only ever receive a literal, which is what stops Jira-sourced data
 * from being translated (the T3 static guard).
 */
export const validationMessage = (t: TFunction, code: ValidationCode): string => {
  switch (code) {
    case 'FIRST_NAME_REQUIRED':
      return t('The first name is required');
    case 'LAST_NAME_REQUIRED':
      return t('The last name is required');
    case 'NAME_TOO_LONG':
      return t('The first and last name exceed 120 characters together: shorten this one.');
    case 'COMPANY_REQUIRED':
      return t('The company is required');
    case 'PHONE_REQUIRED':
      return t('The phone number is required');
    case 'PHONE_INVALID':
      return t('Invalid phone number: digits, spaces and + ( ) . - / only, between 6 and 30 characters.');
    case 'EMAIL_REQUIRED':
      return t('The email address is required');
    case 'EMAIL_INVALID':
      return t('Invalid email address');
    case 'COUNTRY_REQUIRED':
      return t('The country is required');
    case 'AREAS_REQUIRED':
      return t('Select at least one area');
    case 'SPECIALTIES_REQUIRED':
      return t('Select at least one specialty');
    case 'PASSWORD_TOO_SHORT':
      return t('The password must be at least 8 characters');
    case 'PASSWORD_TOO_LONG':
      return t('The password must not exceed 72 bytes');
    default:
      return t('An error occurred');
  }
};
